import React, { useState, useMemo } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { creditTypesFilters } from "../filterConfigurations";

const CreditTypes = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Credit Types"]?.edit;
  const { data: creditTypes, loading, error } = useFirestore("TpCreditType");
  const [selectedCreditType, setSelectedCreditType] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });
  const pageName = "TP Credit Types";

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(creditTypes || [], creditTypesFilters);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilterModel(newFilters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        field: "Credit_Type_abv",
        headerName: "Credit Type Abv",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Credit_Type_Name",
        headerName: "Credit Type Name",
        flex: 1,
        minWidth: 200,
      },
      { field: "Note", headerName: "Note", flex: 1, minWidth: 200 },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((creditType, index) => ({
      id: creditType.id || index, // Use creditType.id if available, otherwise use index
      ...creditType,
    }));
  }, [filteredData]);

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedCreditType(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCreditType(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
  };

  const handleRecordAdded = () => {
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
  };

  const exportColumnOrder = columns.map((col) => col.field);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>Error fetching credit types: {error.message}</p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        {/* Custom Filter Panel */}
        <CustomFilterPanel
          filterConfig={creditTypesFilters}
          onFiltersChange={handleFiltersChange}
          initialFilters={filterModel}
          collapsible={true}
          title="Credit Types Filters"
          resultCount={filteredData?.length || 0}
          totalCount={creditTypes?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="CreditTypes"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={exportColumnOrder}
              exportData={creditTypes}
              templateFields={exportColumnOrder}
            />
          )}
          {canEdit && (
            <ClearCollectionButton collectionName="TpCreditType" />
          )}
        </Box>
        <Box sx={{ height: "calc(100vh - 200px)", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            slots={{
              toolbar: SimpleToolbar,
            }}
            slotProps={{
              toolbar: {
                fileName: "credit-types",
              },
            }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedCreditType && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleEditDialogClose}
          creditTypeDetail={selectedCreditType}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreditTypes;
